import React, { useEffect, useState, useCallback } from "react";
import { connect } from "react-redux";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
// import moment from "moment"; // No longer used directly in this component body after removing SubscribersListItem

import {
  deleteSub,
  getSubscribers,
  // setSubscribersPagination, // No longer directly used if fetchSubscribers handles all cases
  getCompanyDetails,
} from "../../../redux/subscriptions/actions";
// Import the new action
import { getFilteredInsuranceCompanies } from "../../../redux/employees/actions"; 

// import patientLogo from "../../../style/icons/Subscription/patient_active.svg"; // Moved to SubscribersListItem if still needed there
import backArrow from "../../../style/icons/ArrowLeftLight/arrow-left.svg";
import SingleSelectDropdown from "../../components/SingleSelectDropdown/SingleSelectDropdown"; // Import new component

import Paginator from "../../components/Paginator/Paginator";
import Spinner from "../../components/ui/Spinner/Spinner";
import SubscribersListItem from "./SubscribersListItem"; // Assuming this is where the item rendering logic is

import "./SubscribersList.scss";

const SubscribersList = ({
  items,
  deleteSub,
  getSubscribers,
  page_count,
  current_page,
  // setSubscribersPagination, // Removed from props
  currentCompany,
  getCompanyDetails,
  insuranceCompanies,
  getFilteredInsuranceCompaniesAction, // Renamed prop for clarity
}) => {
  const params = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [titleLoading, setTitleLoading] = useState(false);
  const [selectedInsuranceCompany, setSelectedInsuranceCompany] = useState(null);

  const fetchSubscribers = useCallback((companyId, insCompanyId, page) => {
    setLoading(true);
    return getSubscribers(companyId, insCompanyId, page).finally(() => setLoading(false));
  }, [getSubscribers]);

  useEffect(() => {
    setTitleLoading(true);
    // Fetch filtered insurance companies based on the current main company ID
    if (params.id) {
      getFilteredInsuranceCompaniesAction(params.id);
    }

    getCompanyDetails(params.id)
      .catch((error) => console.error("Failed to fetch company details:", error))
      .finally(() => setTitleLoading(false));
    
    // Fetch initial subscribers (no insurance filter, page 1)
    // Ensure selectedInsuranceCompany is null initially or handle its undefined state
    fetchSubscribers(params.id, selectedInsuranceCompany?.id, 1);

  }, [params.id, getCompanyDetails, getFilteredInsuranceCompaniesAction, fetchSubscribers]); // Removed selectedInsuranceCompany dependency here, handled by its own effect

  useEffect(() => {
    if (params.id) {
      fetchSubscribers(params.id, selectedInsuranceCompany?.id, 1);
    }
  }, [selectedInsuranceCompany, params.id, fetchSubscribers]);


  let titleToDisplay;
  if (titleLoading) {
    titleToDisplay = t("titleLoadingPlaceholder", "Завантаження...");
  } else if (
    currentCompany &&
    currentCompany.entity &&
    currentCompany.entity.id &&
    String(currentCompany.entity.id) === params.id &&
    typeof currentCompany.entity.title === 'string' &&
    currentCompany.entity.title.length > 0
  ) {
    titleToDisplay = currentCompany.entity.title;
  } else {
    titleToDisplay = t("subscribers");
  }

  const handleDeleteSubscriber = (subId) => {
    const accept = window.confirm(t("deleteSubText"));
    if (accept) {
      // Ensure the company ID (params.id) is correctly passed
      deleteSub({ company: params.id, subscriber: subId })
        .then(() => {
          // Optionally, re-fetch subscribers after deletion to update the list
          fetchSubscribers(params.id, selectedInsuranceCompany?.id, current_page || 1);
        })
        .catch(error => console.error("Failed to delete subscriber:", error));
    }
  };

  const redirectToSubscriptionList = () => {
    const fromPage = searchParams.get('fromPage');
    if (fromPage) {
      navigate(`/home/admin/subscription?fromPage=${fromPage}`);
    } else {
      navigate("/home/admin/subscription");
    }
  };

  const handleInsuranceCompanyChange = (selectedOption) => {
    setSelectedInsuranceCompany(selectedOption);
  };

  const handlePaginationChange = (paginationArgs) => {
    const pageNumber = paginationArgs.numberOfPage; // Extract the page number
    return fetchSubscribers(params.id, selectedInsuranceCompany?.id, pageNumber); // Ensure this returns the Promise
  };

  return (
    <div className="subscribersListWrapper">
      <div className="title-container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', marginRight: '20px', marginBottom: '10px' }}>
          <img
            className="backArrow"
            src={backArrow}
            alt={t("backAltText", "Назад")}
            onClick={redirectToSubscriptionList}
            style={{ marginRight: '15px', cursor: 'pointer' }}
          />
          <span className="pageTitle" style={{ fontSize: '28px', fontWeight: 'bold' }}>{titleToDisplay}</span>
        </div>
        <div style={{minWidth: '250px', marginBottom: '10px'}}>
          <SingleSelectDropdown
            options={insuranceCompanies || []} // insuranceCompanies prop should now be filtered
            onChange={handleInsuranceCompanyChange}
            value={selectedInsuranceCompany}
            placeholder={t("selectInsuranceCompany")}
            disabled={!insuranceCompanies || insuranceCompanies.length === 0}
          />
        </div>
      </div>
      <div className="subscribersListItemsContainer">
        {loading && (
          <div className="absoluteCenter">
            <Spinner />
          </div>
        )}
        {!loading && items && items.length > 0 ? (
          <SubscribersListItem
            handleDelete={handleDeleteSubscriber}
            items={items}
            t={t}
          />
        ) : (
          !loading && <p className="emptyListPlaceholder">{t("noSubscribersFound", "Передплатників не знайдено")}</p>
        )}
      </div>
      {page_count > 1 && !loading && (
        <Paginator
          current_page={current_page}
          setPagination={handlePaginationChange}
          page_count={page_count}
          intervalDisabled
          setLoading={setLoading}
        />
      )}
    </div>
  );
};

// Note: SubscribersListItem has been moved to its own file or is defined below.
// If it was part of this file and removed, ensure it's correctly imported or defined.

const mapStateToProps = (state) => {
  // The console logs for state.employees can be removed if no longer needed for debugging
  // console.log("SubscribersList mapStateToProps - state.employees:", state.employees);
  let insuranceCompaniesList = [];
  if (state.employees && state.employees.insuranceCompanies) {
    // Assuming insuranceCompanies in state is now the filtered list
    insuranceCompaniesList = state.employees.insuranceCompanies;
  } else if (state.employees && !state.employees.insuranceCompanies) {
    // console.warn("SubscribersList mapStateToProps: state.employees exists, but state.employees.insuranceCompanies is undefined/null.");
  } else {
    // console.warn("SubscribersList mapStateToProps: state.employees is undefined/null.");
  }

  return {
    page_count: state.subscription && state.subscription.page_count,
    current_page: state.subscription && state.subscription.current_page,
    items: state.subscription && state.subscription.items,
    currentCompany: state.subscription && state.subscription.currentCompany,
    insuranceCompanies: insuranceCompaniesList,
    // edit and setEdit props are not typically sourced from Redux unless they control a global edit mode.
    // If they are local to this component or its parent, they should be passed down directly.
  };
};

export default connect(mapStateToProps, {
  // setSubscribersPagination, // Removed
  getSubscribers,
  deleteSub,
  getCompanyDetails,
  // Map the new action to the prop
  getFilteredInsuranceCompaniesAction: getFilteredInsuranceCompanies, 
})(SubscribersList);
